import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import TopMenuBar from "../components/TopMenuBar";
import WelcomeGoldModal from "../components/WelcomeGoldModal";
import { getCurrentUserAndProfile, handleSignOut, isUserGold } from "../queries/Queries.js";
import { handleUpgrade } from "../queries/stripeQueries";
import { PRICING_CONFIG } from "../constants/pricing";
import { 
  Bot, 
  Swords, 
  Landmark, 
  ArrowRight, 
  LogIn, 
  Crown,
  Sparkles,
  Star,
  Check,
  X,
  Brain
} from "lucide-react";
import styles from "../styles/LandingPage.module.css";
import einsteinAvatar from '../assets/einstein.jpeg';
import nietzscheAvatar from '../assets/nietzsche.jpeg';
import philosopherAvatar from '../assets/philosopher.jpeg';

import user1Avatar from '../assets/discord_logo.png';
import user2Avatar from '../assets/tiktok_logo.png';
import user3Avatar from '../assets/ig_logo.png';

const SocialProofCard = ({ platform, username, content, rating, avatar }) => {
  return (
    <div className={styles.socialProofCard}>
      <div className={styles.socialHeader}>
        <div className={styles.socialUser}>
          <img src={avatar} alt={`${username}'s avatar`} className={styles.socialAvatar} />
          <div>
            <div className={styles.socialUsername}>@{username}</div>
            <div className={styles.socialPlatform}>{platform}</div>
          </div>
        </div>
        {rating && (
          <div className={styles.socialRating}>
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={14} className={i < rating ? styles.starFilled : styles.starEmpty} />
            ))}
          </div>
        )}
      </div>
      <p className={styles.socialContent}>{content}</p>
    </div>
  );
};

const FeatureCard = ({ icon, title, description, isGold, onClick }) => {
  return (
    <div 
      className={`${styles.featureCard} ${isGold ? styles.goldFeature : ''}`}
      onClick={onClick}
    >
      {isGold && (
        <div className={styles.goldBadge}>
          <Crown size={16} />
          <span>GOLD</span>
        </div>
      )}
      <div className={styles.featureIcon}>{icon}</div>
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  );
};

const ComparisonItem = ({ feature, basic, gold }) => {
  return (
    <div className={styles.comparisonRow}>
      <div className={styles.comparisonFeature}>{feature}</div>
      <div className={styles.comparisonValue}>
        {basic ? <Check className={styles.checkIcon} /> : <X className={styles.xIcon} />}
      </div>
      <div className={styles.comparisonValue}>
        {gold ? <Check className={styles.checkIconGold} /> : <X className={styles.xIcon} />}
      </div>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userAndProfile, setUserAndProfile] = useState(null);
  const [showGoldModal, setShowGoldModal] = useState(false);
  const [goldStatus, setGoldStatus] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isYearly, setIsYearly] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const result = await getCurrentUserAndProfile();
        setUserAndProfile(result);
      } catch (error) {
        console.error("Error initializing:", error);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    const fetchGoldStatus = async () => {
      if (userAndProfile?.user) {
        try {
          const { isGold } = await isUserGold(userAndProfile.user.id);
          setGoldStatus(isGold);
        } catch {
          setGoldStatus(false);
        }
      } else {
        setGoldStatus(false);
      }
    };
    fetchGoldStatus();
  }, [userAndProfile]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('welcome') === 'gold') {
      setShowGoldModal(true);
    }
  }, [location]);

  const handleSignOutClick = async () => {
    const success = await handleSignOut();
    if (success) {
      setUserAndProfile(null);
      setGoldStatus(false);
      navigate("/");
    }
  };

  const handlePlayAI = () => {
    navigate("/ai");
  };

  const handleSignUp = () => {
    navigate("/signup");
  };

  const handleGoldFeatureClickBP = () => {
    if (!userAndProfile) {
      alert("Please login using the menu bar to upgrade your subscription");
    } else if (!goldStatus) {
      window.location.href = "/checkout";
    } else {
      navigate("/bpai");
    }
  };

  const handleGoldFeatureClickJudge = () => {
    if (!userAndProfile) {
      alert("Please log in to upgrade your subscription");
    } else if (!goldStatus) {
      window.location.href = "/checkout";
    }
    // if goldStatus && user, do nothing
  };

  const handleUpgradeClick = async () => {
    if (!userAndProfile) {
      alert("Please log in to upgrade your subscription");
      return;
    }
    if (goldStatus) {
      return; // do nothing if already gold
    }
    try {
      setIsUpgrading(true);
      await handleUpgrade(isYearly);
    } catch (error) {
      console.error("Error:", error);
      alert("Please log in to upgrade your subscription");
    } finally {
      setIsUpgrading(false);
    }
  };

  const socialProofs = [
    {
      platform: "Discord",
      username: "Poposagrado",
      content: "I like debating and wanted to improve my skills. The AI opponents are surprisingly challenging 🔥",
      rating: 5,
      avatar: user1Avatar
    },
    {
      platform: "TikTok",
      username: "vin",
      content: "hey man your idea is literally golden. I was on your website for hours trying this out.",
      rating: 5,
      avatar: user2Avatar
    },
    {
      platform: "Instagram",
      username: "mnguyen12308",
      content: "love the blitz mode, the AI keeps defeating me lol. gonna try debating my friends next, this is awesome!",
      rating: 5,
      avatar: user3Avatar
    }
  ];

  const comparisonFeatures = [
    { feature: "Unlimited Debates vs Live Opponents or AI", basic: true, gold: true },
    { feature: "Basic Debate Judging and Feedback", basic: true, gold: true },
    { feature: "50+ AI Personas for Every Skill Level", basic: false, gold: true },
    { feature: "Unlimited Debate Evaluations with a State-of-the-Art Judge", basic: false, gold: true },
    { feature: "Mini British Parliamentary Debates on Any Motion", basic: false, gold: true },
    { feature: "Custom Debate Turn Times for Better Practice", basic: false, gold: true },
    { feature: "Premium Cosmetics and Animations", basic: false, gold: true },
  ];

  const currentPricing = isYearly ? PRICING_CONFIG.yearly : PRICING_CONFIG.monthly;

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loader}></div>
      </div>
    );
  }

  return (
    <div className={styles.landingPage}>
      <TopMenuBar
        userAndProfile={userAndProfile}
        onSignOut={handleSignOutClick}
      />

      <main className={styles.mainContent}>
        {/* 1. HERO SECTION */}
        <section className={styles.heroSection}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>
              Master the art of debate with AI
            </h1>
            <p className={styles.heroSubtitle}>
              Challenge your friends or historical personas to an online debate <br/>
              Get real-time analysis to sharpen your skills
            </p>
            
            <div className={styles.heroButtons}>
              {!userAndProfile && (
                <button className={styles.primaryButton} onClick={handleSignUp}>
                  <LogIn size={20} />
                  Sign Up
                </button>
              )}
              <button className={styles.secondaryButton} onClick={handlePlayAI}>
                <Bot size={20} />
                Practice vs AI
              </button>
              <button
                className={styles.secondaryButton}
                onClick={() =>
                  userAndProfile
                    ? navigate("/rooms")
                    : navigate("/signup")
                }
              >
                <Swords size={20} />
                Play Online
              </button>
            </div>

            <div className={styles.heroStats}>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>1K+</span>
                <span className={styles.statLabel}>Debaters</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>50K+</span>
                <span className={styles.statLabel}>Debates Completed</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>∞</span>
                <span className={styles.statLabel}>Topics</span>
              </div>
            </div>
          </div>
        </section>

        {/* 2. FEATURES SECTION */}
        <section className={styles.featuresSection}>
          <div className={styles.sectionHeader}>
            <h2>Everything you need to become a debate champion</h2>
            <p>Practice, compete, and improve with our comprehensive features</p>
          </div>

          <div className={styles.featuresGrid}>
            <FeatureCard
              icon={<Swords size={32} />}
              title="Debate Online"
              description="Challenge your friends to a live debate on any topic"
              onClick={() => userAndProfile ? navigate("/rooms") : navigate("/signup")}
            />
            
            <FeatureCard
              icon={
                <div className={styles.personaIcons}>
                  <img src={einsteinAvatar} alt="Einstein" />
                  <img src={nietzscheAvatar} alt="Nietzsche" />
                  <img src={philosopherAvatar} alt="Philosopher" />
                </div>
              }
              title="AI Personas"
              description="Debate against historical personas tailored to your level"
              onClick={handlePlayAI}
            />
            
            <FeatureCard
              icon={<Landmark size={32} />}
              title="British Parliamentary"
              description="Verse the AI in a mini BP debate on any motion of your choice"
              isGold={true}
              onClick={handleGoldFeatureClickBP}
            />
            
            <FeatureCard
              icon={<Brain size={32} />}
              title="AI Debate Judge"
              description="Determine win/loss, evaluate your arguments and improve"
              isGold={true}
              onClick={handleGoldFeatureClickJudge}
            />
          </div>
        </section>

        {/* 3. VIDEO SALES LETTER */}
        <section className={styles.videoSection}>
          <div className={styles.videoWrapper}>
            <iframe
              className={styles.responsiveIframe}
              src="https://www.youtube.com/embed/UNQyRPDnL4k"
              title="Debate Arena Demo"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </section>

        {/* 4. TESTIMONIALS (SOCIAL PROOF) */}
        <section className={styles.socialProofSection}>
          <div className={styles.socialProofGrid}>
            {socialProofs.map((proof, index) => (
              <SocialProofCard key={index} {...proof} />
            ))}
          </div>
        </section>

        {/* 5. CTA SECTION */}
        <section className={styles.ctaSection}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>Ready to elevate your debate game?</h2>
            <p>Choose the plan that's right for you</p>
            
            {/* Pricing Toggle */}
            <div className={styles.pricingToggleContainer}>
              <div className={styles.pricingToggleGroup}>
                <button
                  className={`${styles.pricingToggleButton} ${!isYearly ? styles.activePricing : ''}`}
                  onClick={() => setIsYearly(false)}
                >
                  Monthly
                </button>
                <button
                  className={`${styles.pricingToggleButton} ${isYearly ? styles.activePricing : ''}`}
                  onClick={() => setIsYearly(true)}
                >
                  Yearly
                  {!isYearly && <span className={styles.pricingDiscount}>Save 50%</span>}
                </button>
              </div>
            </div>
            
            <div className={styles.plansComparison}>
              <div className={styles.planHeader}>
                <div className={styles.planHeaderFeature}></div>
                <div className={styles.planHeaderOption}>
                  <div className={styles.goldHeaderBadge}><span></span></div>
                  <h3>Basic</h3>
                  <div className={styles.priceDisplay}>
                    <span className={styles.price}>$0</span>
                    <span className={styles.pricePeriod}>/forever</span>
                  </div>
                </div>
                <div className={`${styles.planHeaderOption} ${styles.goldHeader}`}>
                  <div className={styles.goldHeaderBadge}><span>Early Access</span></div>
                  <h3>Gold</h3>
                  <div className={styles.priceComparison}>
                    <div className={styles.pricingFlexRow}>
                      <span className={styles.originalPrice}>${currentPricing.gold.originalPrice}</span>
                      <span className={styles.pricingArrow}>→</span>
                      <div className={styles.goldPriceDisplay}>
                        <span className={styles.goldPrice}>${currentPricing.gold.price}</span>
                        <span className={styles.pricePeriod}>/{currentPricing.gold.period}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {comparisonFeatures.map((item, index) => (
                <ComparisonItem key={index} {...item} />
              ))}
            </div>

            <button 
              className={styles.ctaButton}
              onClick={handleUpgradeClick}
              disabled={isUpgrading || goldStatus}
            >
              {goldStatus ? (
                "Thanks for being a Gold Member"
              ) : isUpgrading ? (
                "Loading..."
              ) : (
                <>
                  <Sparkles size={20} />
                  Upgrade to Gold
                  <Crown size={20} />
                </>
              )}
            </button>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <Link to="/terms-of-use" className={styles.footerLink}>
          terms of use
        </Link>
        <span className={styles.footerSeparator}>|</span>
        <Link to="/privacy-policy" className={styles.footerLink}>
          privacy policy
        </Link>
      </footer>

      <WelcomeGoldModal 
        isOpen={showGoldModal}
        onClose={() => setShowGoldModal(false)}
      />
    </div>
  );
};

export default LandingPage;
